/**
 * ViewCohortEventRsvpsModal Component
 * Modal for viewing RSVP responses for a cohort event (MED-92 / E8-14)
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Stack,
  Group,
  Button,
  Text,
  Table,
  Badge,
  SegmentedControl,
  Center,
  Loader,
} from '@mantine/core';
import {
  cohortEventsApi,
  type CohortEventRsvpListResponse,
  type CohortEventRsvpDetail,
} from '../../../../services/api/cohortEvents';

interface ViewCohortEventRsvpsModalProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  eventId: string | null;
  eventName: string;
}

type RsvpFilter = 'all' | 'accepted' | 'declined' | 'pending' | 'tentative';

const rsvpBadgeConfig: Record<string, { color: string; label: string }> = {
  accepted: { color: 'green', label: 'Accepted' },
  declined: { color: 'red', label: 'Declined' },
  pending: { color: 'gray', label: 'Pending' },
  tentative: { color: 'yellow', label: 'Tentative' },
};

const recipientTypeBadge: Record<string, { color: string; label: string }> = {
  coach: { color: 'blue', label: 'Coach' },
  participant: { color: 'grape', label: 'Participant' },
  external: { color: 'gray', label: 'External' },
};

export function ViewCohortEventRsvpsModal({
  opened,
  onClose,
  cohortId,
  eventId,
  eventName,
}: ViewCohortEventRsvpsModalProps) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<CohortEventRsvpListResponse | null>(null);
  const [filter, setFilter] = useState<RsvpFilter>('all');

  useEffect(() => {
    if (!opened || !eventId) return;

    const loadRsvps = async () => {
      setLoading(true);
      try {
        const result = await cohortEventsApi.getRsvps(cohortId, eventId);
        setData(result);
      } catch {
        // Silently handle
      } finally {
        setLoading(false);
      }
    };

    loadRsvps();
    setFilter('all');
  }, [opened, cohortId, eventId]);

  const filteredInvitations = useMemo((): CohortEventRsvpDetail[] => {
    if (!data) return [];
    if (filter === 'all') return data.invitations;
    return data.invitations.filter((inv) => inv.rsvp_status === filter);
  }, [data, filter]);

  const summary = data?.summary;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Stack gap={4}>
          <Text fw={600} size="lg">
            RSVPs: {eventName}
          </Text>
          {summary && (
            <Text size="sm" c="dimmed">
              {summary.accepted} confirmed, {summary.declined} declined, {summary.pending} pending
              out of {summary.total} invited
            </Text>
          )}
        </Stack>
      }
      size="lg"
    >
      {loading ? (
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      ) : !data || data.invitations.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="xl">
          No invitations found
        </Text>
      ) : (
        <Stack gap="md">
          {/* Filter */}
          <SegmentedControl
            value={filter}
            onChange={(v) => setFilter(v as RsvpFilter)}
            data={[
              { label: `All (${summary?.total || 0})`, value: 'all' },
              { label: `Accepted (${summary?.accepted || 0})`, value: 'accepted' },
              { label: `Declined (${summary?.declined || 0})`, value: 'declined' },
              { label: `Pending (${summary?.pending || 0})`, value: 'pending' },
            ]}
            size="sm"
          />

          {/* Table */}
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Name</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>RSVP Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filteredInvitations.map((inv) => {
                const rsvpConfig = rsvpBadgeConfig[inv.rsvp_status] || rsvpBadgeConfig.pending;
                const typeConfig = recipientTypeBadge[inv.recipient_type] || recipientTypeBadge.external;
                return (
                  <Table.Tr key={inv.id}>
                    <Table.Td>{inv.recipient_name || '—'}</Table.Td>
                    <Table.Td>
                      <Text size="sm">{inv.email}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" variant="light" color={typeConfig.color}>
                        {typeConfig.label}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge size="sm" variant="light" color={rsvpConfig.color}>
                        {rsvpConfig.label}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>

          {filteredInvitations.length === 0 && (
            <Text size="sm" c="dimmed" ta="center">
              No invitations match the selected filter
            </Text>
          )}

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={onClose}>
              Close
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

export default ViewCohortEventRsvpsModal;
